import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Pagination, PaginationContent, PaginationItem } from '../../../components/ui/pagination';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { getContacts, Contact } from '../services/contactsApi';
import { stagesApi, Stage } from '../../inbox/services/stagesApi';
import ChatContactInfoModal from './ChatContactInfoModal';

/**
 * Converts a hex color (#RRGGBB) to a light background + readable text style.
 */
function hexToTagStyle(hex: string) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return {
        bg: `rgba(${r}, ${g}, ${b}, 0.12)`,
        text: hex,
        dot: hex,
    };
}

const ContactsTable: React.FC = () => {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [stageColors, setStageColors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            getContacts(),
            stagesApi.fetchStages(),
        ])
            .then(([contactsData, stagesData]) => {
                setContacts(contactsData);
                const colorMap: Record<string, string> = {};
                stagesData.forEach((s) => { if (s.color) colorMap[s.name] = s.color; });
                setStageColors(colorMap);
            })
            .catch((err) => console.error('[ContactsTable] Error:', err))
            .finally(() => setLoading(false));
    }, []);

    const totalPages = Math.max(1, Math.ceil(contacts.length / pageSize));
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedContacts = contacts.slice(startIndex, startIndex + pageSize);
    const showingFrom = contacts.length === 0 ? 0 : startIndex + 1;
    const showingTo = Math.min(startIndex + pageSize, contacts.length);

    const mapContactToModal = (contact: Contact) => ({
        name: contact.nombre,
        company: contact.company || '',
        email: contact.correo,
        phone: contact.celular,
        value: contact.value || '',
        stage: contact.estado,
    });

    if (loading) {
        return (
            <div className="bg-white rounded-2xl shadow p-8 flex items-center justify-center min-h-[40vh]">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-maxi-text-light text-sm">Cargando contactos...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-end my-5 gap-2">
                <select
                    id="pageSize"
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-maxi-green"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                >
                    {[10, 20, 30, 50, 100].map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                <label htmlFor="pageSize" className="text-sm text-maxi-text-dark font-medium mr-2">Entradas por página</label>
            </div>
            <div className="bg-white rounded-2xl shadow overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="h-[6vh] border-b border-gray-200">
                            <TableHead className="text-maxi-text-dark font-semibold w-[18%]">Nombre</TableHead>
                            <TableHead className="text-maxi-text-dark font-semibold w-[18%]">Correo</TableHead>
                            <TableHead className="text-maxi-text-dark font-semibold w-[14%]">Celular</TableHead>
                            <TableHead className="text-maxi-text-dark font-semibold w-[18%]">Última Interacción</TableHead>
                            <TableHead className="text-maxi-text-dark font-semibold w-[16%]">Agente</TableHead>
                            <TableHead className="text-maxi-text-dark font-semibold w-[16%]">Etiqueta</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedContacts.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center text-maxi-text-light py-8">
                                    No hay contactos registrados
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedContacts.map((contact) => {
                                const hex = stageColors[contact.estado] || '#9CA3AF';
                                const style = hexToTagStyle(hex);
                                return (
                                    <TableRow
                                        key={contact.id}
                                        className="h-[6vh] hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => {
                                            setSelectedContact(contact);
                                            setModalOpen(true);
                                        }}
                                    >
                                        <TableCell className="text-maxi-text-dark font-medium">{contact.nombre}</TableCell>
                                        <TableCell className="text-maxi-text-dark text-sm">{contact.correo || <span className="text-gray-400">—</span>}</TableCell>
                                        <TableCell className="text-maxi-text-dark text-sm">{contact.celular}</TableCell>
                                        <TableCell className="text-maxi-text-dark text-sm">{contact.ultimaInteraccion}</TableCell>
                                        <TableCell className="text-maxi-text-dark text-sm">{contact.agente}</TableCell>
                                        <TableCell>
                                            <span
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                                                style={{ backgroundColor: style.bg, color: style.text }}
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: style.dot }} />
                                                {contact.estado}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-between px-2 pt-3 pb-2 w-full">
                <span className="text-sm text-maxi-text">
                    Mostrando {showingFrom} a {showingTo} de {contacts.length} entradas
                </span>
                <div className="flex-1" />
                <div className="flex items-center gap-2">
                    <Pagination>
                        <PaginationContent className="flex gap-1">
                            <PaginationItem>
                                <button
                                    className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 hover:bg-muted disabled:opacity-50 transition-colors"
                                    disabled={currentPage <= 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                            </PaginationItem>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                                Math.max(0, currentPage - 3),
                                currentPage + 2
                            ).map(page => (
                                <PaginationItem key={page}>
                                    <button
                                        className={`flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                                            page === currentPage
                                                ? 'border-primary bg-primary/10 font-semibold text-maxi-text-dark'
                                                : 'border-gray-200 hover:bg-muted'
                                        }`}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                </PaginationItem>
                            ))}
                            <PaginationItem>
                                <button
                                    className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 hover:bg-muted disabled:opacity-50 transition-colors"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            </div>
            {selectedContact && (
                <ChatContactInfoModal
                    open={modalOpen}
                    contact={mapContactToModal(selectedContact)}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedContact(null);
                    }}
                />
            )}
        </div>
    );
};

export default ContactsTable;
